import React, { useCallback, useMemo, useRef, useState } from 'react';
import StatePill from '../StatePill';
import { initialsOf } from '../../utils/locations';

// Sign out devices / Transfer a device, per the Locations redesign.
//
// Three numbered steps: who is taking them, which devices, and what for.
//
// The point of the screen over the old modal is that every scanned serial is
// resolved against real stock before anything is committed. A serial can come
// back five ways, and the difference matters: "already out with Dan" is a
// different problem from "not a device we own", and the old modal could express
// neither — it only offered a dropdown of devices already known to be free.
//
// Blocking states stop the commit rather than being silently dropped, so you
// cannot hand over a device the system thinks is somewhere else without first
// resolving where it actually is.
//
// The one exception is "On site". A device the OWL portal API has placed at a
// site can still be physically with a named person, and that location is owned
// by the portal — moving it to "Employee Assignment" would just be overwritten
// or put Bench out of step with the portal. So an On site row can be
// overridden: the device is assigned to the person but keeps its location.

// Tone per resolution outcome. Keyed to the pill tones already in the CSS.
const TONE = {
  'In stock': 'success',
  'Already out': 'warning',
  'On site': 'warning',
  'Not in stock': 'danger',
  'Current holder': 'info',
  'Override': 'brand',
};

function Step({ n, title, sub, right, children }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border-default)' }}>
        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 9999, background: 'var(--brand-default)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }} className="tnum">{n}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
          {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function SignOutDevices({
  st, mode = 'signout', presetEmployeeId = null, presetSerials = [], fromEmployeeId = null,
  onCancel, onCommit, onAddEmployee,
}) {
  const transfer = mode === 'transfer';

  const [empId, setEmpId] = useState(presetEmployeeId || '');
  const [personQuery, setPersonQuery] = useState('');
  const [serials, setSerials] = useState(presetSerials);
  const [scan, setScan] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  // Serials (lower-cased) whose On site block has been overridden.
  const [overrides, setOverrides] = useState([]);
  const scanRef = useRef(null);

  // Memoised: the fallback [] would otherwise be a new array on every render,
  // invalidating the serial resolver's dependencies each time.
  const employees = useMemo(() => st.employees || [], [st.employees]);
  const empById = id => employees.find(e => e.id === id);

  // Open assignments, i.e. what is actually out with someone right now.
  const openHolds = useMemo(() => {
    const out = [];
    (st.assignments || []).filter(a => !a.returnedAt).forEach(a => {
      (a.deviceIds || []).forEach(id => {
        if ((a.returnedDeviceIds || []).includes(id)) return;
        out.push({ deviceId: id, employeeId: a.employeeId, assignmentId: a.id, since: a.assignedAt,
          keptLocation: (a.keptLocationDeviceIds || []).includes(id) });
      });
    });
    return out;
  }, [st.assignments]);

  const heldCount = id => openHolds.filter(h => h.employeeId === id).length;

  // Resolve a typed/scanned serial against real stock.
  //
  // "In stock" mirrors the old modal's availDevs rule — a device is free to sign
  // out when it is sitting In Stock or In Testing and is not already on an open
  // assignment. Everything else is reported with the reason, not just refused.
  const resolve = useCallback(serial => {
    const key = String(serial).trim().toLowerCase();
    const d = (st.devices || []).find(
      x => String(x.serial || '').toLowerCase() === key || String(x.deviceId || '').toLowerCase() === key
    );
    if (!d) return { state: 'Not in stock', detail: 'No device with this serial, or it has been written off.', blocks: true };

    const hold = openHolds.find(h => h.deviceId === d.id);
    if (hold) {
      const holder = employees.find(e => e.id === hold.employeeId);
      const who = (holder && holder.name) || 'someone else';
      if (transfer && hold.employeeId === fromEmployeeId) {
        return { device: d, state: 'Current holder', detail: `Held by ${who}. Transferring takes it off them in the same step.`, blocks: false, fromAssignmentId: hold.assignmentId, keepLocation: hold.keptLocation };
      }
      return { device: d, state: 'Already out', detail: `Held by ${who} since ${hold.since ? new Date(hold.since).toLocaleDateString('en-GB') : 'an earlier date'}. Use Transfer instead.`, blocks: true };
    }

    if (d.status === 'Quarantine') return { device: d, state: 'Not in stock', detail: 'In quarantine — needs a bench check before it goes out.', blocks: true };
    if (d.status === 'Pending RMA' || d.status === 'With Manufacturer' || d.availability === 'rma') {
      return { device: d, state: 'Not in stock', detail: 'On an RMA — not available to hand over.', blocks: true };
    }
    if (d.availability === 'lost') return { device: d, state: 'Not in stock', detail: 'Recorded as lost. Restoring it needs an admin.', blocks: true };
    if (d.location && d.location !== 'In Stock' && d.location !== 'In Testing') {
      if (overrides.includes(key)) {
        return { device: d, state: 'Override', detail: `Stays at ${d.location} for the OWL portal — assigned to the person alongside it.`, blocks: false, keepLocation: true, overridable: true };
      }
      return { device: d, state: 'On site', detail: `Deployed to ${d.location}. Bring it back first, or assign anyway if the portal manages its location.`, blocks: true, overridable: true };
    }
    return { device: d, state: 'In stock', detail: 'In stock at the Onwave office, ready to hand over.', blocks: false };
  }, [st.devices, openHolds, employees, transfer, fromEmployeeId, overrides]);

  const toggleOverride = serial => {
    const k = String(serial).trim().toLowerCase();
    setOverrides(prev => (prev.includes(k) ? prev.filter(x => x !== k) : prev.concat(k)));
  };

  const rows = useMemo(() => serials.map(s => ({ serial: s, ...resolve(s) })), [serials, resolve]);
  const blocked = rows.filter(r => r.blocks);

  const addSerial = raw => {
    // Accept a pasted comma/space separated list as well as one scan at a time.
    const parts = String(raw).split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    if (!parts.length) return;
    setSerials(prev => prev.concat(parts.filter(p => !prev.some(x => x.toLowerCase() === p.toLowerCase()))));
    setScan('');
  };

  const person = empId ? empById(empId) : null;
  const pool = employees.filter(e => !(transfer && e.id === fromEmployeeId));
  const pq = personQuery.trim().toLowerCase();
  const candidates = pq
    ? pool.filter(e => `${e.name || ''} ${e.role || ''} ${e.team || ''}`.toLowerCase().includes(pq))
    : pool;

  const purposeVal = purpose.trim();
  const ready = !!empId && rows.length > 0 && blocked.length === 0 && !!purposeVal;

  const gateNote = ready
    ? 'Each device gets a log entry against the person, with the purpose and today’s date.'
    : !empId ? 'Pick a person first.'
      : rows.length === 0 ? 'Scan at least one device.'
        : blocked.length ? `${blocked.length} device${blocked.length === 1 ? '' : 's'} cannot be signed out — remove ${blocked.length === 1 ? 'it' : 'them'} or resolve ${blocked.length === 1 ? 'it' : 'them'} first.`
          : 'Record what the devices are for.';

  const btnGhost = {
    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
    borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };

  const summary = [
    { k: transfer ? 'New holder' : 'Person', v: person ? person.name : 'Not picked', set: !!person },
    { k: 'Devices', v: String(rows.length), set: rows.length > 0 },
    { k: 'Blocked', v: blocked.length ? String(blocked.length) : 'None', set: true, danger: blocked.length > 0 },
    ...(rows.some(r => r.state === 'Override') ? [{ k: 'Kept on site', v: String(rows.filter(r => r.state === 'Override').length), set: true }] : []),
    { k: 'Purpose', v: purposeVal || 'Not set', set: !!purposeVal },
    { k: 'Expected back', v: expectedReturn ? new Date(expectedReturn).toLocaleDateString('en-GB') : 'Not set', set: !!expectedReturn },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button type="button" onClick={onCancel} style={{ ...btnGhost, marginTop: 2 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>
            {transfer ? 'Transfer a device' : 'Sign out devices'}
          </div>
          <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--text-muted)', marginTop: 4, maxWidth: 640 }}>
            {transfer
              ? 'Move a device from one person to another. It comes off the current holder in the same step, so it is never counted twice.'
              : 'Onwave staff request devices and take them off site. Recording it here is what keeps the fleet count honest.'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          <Step
            n={1}
            title={transfer ? 'New holder' : 'Who is taking them'}
            sub={transfer
              ? `Currently with ${(empById(fromEmployeeId) || {}).name || 'someone else'}.`
              : 'Employees, not Bench users — anyone on the list can hold a device.'}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {person ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 6, background: 'var(--brand-tint)', border: '1px solid var(--brand-border)' }}>
                  <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--brand-default)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{initialsOf(person.name)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-light)' }}>{person.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--brand-light)', marginTop: 2 }}>
                      {[person.role, person.team].filter(Boolean).join(' · ') || 'No role recorded'} · holding {heldCount(person.id)}
                    </div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEmpId(''); setPersonQuery(''); }}>Change</button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 10px', border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--text-muted)' }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    <input
                      value={personQuery}
                      onChange={e => setPersonQuery(e.target.value)}
                      placeholder="Search by name, team or role"
                      aria-label="Search employees"
                      style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }} />
                    <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{candidates.length} of {pool.length}</span>
                  </div>
                  <div style={{ border: '1px solid var(--border-default)', borderRadius: 6, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
                    {candidates.map(e => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setEmpId(e.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 44, padding: '0 12px', cursor: 'pointer', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border-subtle)', fontFamily: 'inherit', textAlign: 'left' }}
                        onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--surface-raised)'; }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}>
                        <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--text-subtle)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{initialsOf(e.name)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[e.role, e.team].filter(Boolean).join(' · ') || 'No role recorded'}
                          </div>
                        </div>
                        <span className="tnum" style={{ flexShrink: 0, fontSize: 12, color: heldCount(e.id) ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {heldCount(e.id) ? `holds ${heldCount(e.id)}` : 'holds none'}
                        </span>
                      </button>
                    ))}
                    {candidates.length === 0 && (
                      <div style={{ padding: '20px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No one matches</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                          {employees.length === 0 ? 'No employees yet.' : 'Try a different name, team or role.'}
                        </div>
                        {onAddEmployee && (
                          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={onAddEmployee}>Add employee</button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Step>

          <Step
            n={2}
            title="Devices"
            sub={transfer
              ? 'Fixed for a transfer — one device at a time.'
              : 'Scan each device the person is taking. Every serial is checked against stock first.'}
            right={<span className="tnum" style={{ flexShrink: 0, fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{rows.length} device{rows.length === 1 ? '' : 's'}</span>}>
            {!transfer && (
              <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', border: '1.5px solid var(--brand-default)', borderRadius: 6, background: 'var(--surface-default)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--brand-light)' }}><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="3" y1="12" x2="21" y2="12" /></svg>
                  <input
                    ref={scanRef}
                    value={scan}
                    onChange={e => setScan(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerial(scan); } }}
                    placeholder="Scan or type a serial…"
                    aria-label="Scan a serial"
                    className="tnum"
                    style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }} />
                  <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-muted)' }}>Enter</span>
                </div>
              </div>
            )}
            {rows.map(r => (
              <div key={r.serial} style={{ display: 'grid', gridTemplateColumns: '118px 64px 150px minmax(0,1fr) auto', gap: '0 14px', alignItems: 'center', minHeight: 48, padding: '7px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{r.serial}</div>
                <div><span className="pill pill-neutral" style={{ fontSize: 11.5 }}>{(r.device && r.device.type) || '—'}</span></div>
                <div><StatePill state={r.state} tone={TONE[r.state] || 'neutral'} /></div>
                <div style={{ minWidth: 0, fontSize: 12, color: 'var(--text-muted)' }}>{r.detail}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                  {!transfer && r.overridable && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleOverride(r.serial)}
                      title={r.state === 'Override' ? 'Block it again' : 'Assign to the person but leave its location as the OWL portal set it'}>
                      {r.state === 'Override' ? 'Undo override' : 'Assign anyway'}
                    </button>
                  )}
                  {!transfer && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSerials(prev => prev.filter(x => x !== r.serial)); setOverrides(prev => prev.filter(x => x !== String(r.serial).trim().toLowerCase())); }}>Remove</button>
                  )}
                </div>
              </div>
            ))}
            {rows.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6, padding: '32px 24px' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No devices yet</div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)', maxWidth: 320 }}>
                  Scan what the person is taking. Anything already out with someone else is flagged rather than blocked.
                </div>
              </div>
            )}
          </Step>

          <Step n={3} title="What for" sub="Recorded against every device in this batch, so the reason survives the handover.">
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 190px', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <label style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                  Purpose<span style={{ color: 'var(--brand-light)' }}>*</span>
                </label>
                <input
                  value={purpose}
                  onChange={e => setPurpose(e.target.value)}
                  placeholder="e.g. Fratton survey standby"
                  aria-invalid={!purposeVal || undefined}
                  style={{ height: 34, padding: '0 10px', borderRadius: 6, outline: 'none', border: `1px solid ${purposeVal ? 'var(--border-default)' : 'var(--danger-border)'}`, background: 'var(--surface-default)', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14 }} />
                <span style={{ fontSize: 12, color: purposeVal ? 'var(--text-muted)' : 'var(--danger-text)' }}>{purposeVal ? '' : 'Required'}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Expected back</label>
                <input
                  type="date"
                  value={expectedReturn}
                  onChange={e => setExpectedReturn(e.target.value)}
                  style={{ height: 34, padding: '0 10px', borderRadius: 6, outline: 'none', border: '1px solid var(--border-default)', background: 'var(--surface-default)', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14 }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Optional</span>
              </div>
            </div>
          </Step>
        </div>

        <div style={{ flex: '0 1 328px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Summary</span>
            </div>
            {summary.map(f => (
              <div key={f.k} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{f.k}</span>
                <span style={{ fontSize: 14, fontWeight: 500, textAlign: 'right', color: f.danger ? 'var(--danger-text)' : f.set ? 'var(--text-primary)' : 'var(--text-muted)' }}>{f.v}</span>
              </div>
            ))}
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => { if (ready) onCommit({ employeeId: empId, rows, purpose: purposeVal, expectedReturn, transfer }); }}
                disabled={!ready}
                className="btn btn-primary"
                style={{ height: 40, justifyContent: 'center', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                {transfer ? 'Transfer the device' : `Sign out ${rows.length} device${rows.length === 1 ? '' : 's'}`}
              </button>
              <button type="button" onClick={onCancel} className="btn btn-ghost" style={{ height: 32, justifyContent: 'center' }}>Cancel</button>
              <span style={{ fontSize: 12, lineHeight: 1.5, color: ready ? 'var(--text-muted)' : 'var(--danger-text)' }}>{gateNote}</span>
            </div>
          </div>

          <div className="card" style={{ padding: '14px 16px' }}>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              {transfer
                ? 'A transferred device stays out of stock throughout — it simply changes hands. The old holder’s count drops by one as the new holder’s rises.'
                : 'Signed-out devices leave stock but do not go to a site, so they count as with staff rather than deployed.'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignOutDevices;
